import { styled } from '@linaria/react';
import type { ReactNode } from 'react';
import { inkDarkest, inkLight, inkLighter, skyLight } from '../../../styles/design-tokens';
import { Icon } from '../../../ui-library/icon/icon';

type StatTileProps = {
  /** Optional Material icon rendered before the label. */
  iconName?: string;
  label: string;
  value: ReactNode;
  /** Overrides the default value color (e.g. an authorship or survival accent). */
  valueColor?: string;
  subtitle?: ReactNode;
  /**
   * Renders the value as a single truncated 14px line (for long strings like a
   * user name or timestamp) instead of the default large numeric style.
   */
  truncate?: boolean;
};

/**
 * The shared page-layout overview tile: a bordered card with an uppercase label
 * (optional icon), a value, and an optional subtitle. Used by both the session
 * Overview panel and the pull-request tab so both surfaces stay visually locked.
 *
 * @param {StatTileProps} props - Tile label, value, and presentation options
 * @returns {JSX.Element} The rendered stat tile
 */
export function StatTile({ iconName, label, value, valueColor, subtitle, truncate = false }: StatTileProps) {
  return (
    <Tile>
      <TileLabel>
        {iconName && <Icon name={iconName} size={12} color={inkLight} />}
        {label}
      </TileLabel>
      {truncate ? (
        <TileText title={typeof value === 'string' ? value : undefined} valueColor={valueColor}>
          {value}
        </TileText>
      ) : (
        <TileValue valueColor={valueColor}>{value}</TileValue>
      )}
      {subtitle != null && <TileSub>{subtitle}</TileSub>}
    </Tile>
  );
}

export const StatTileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 16px;
`;

const Tile = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border: 1px solid ${skyLight};
  border-radius: 8px;
  min-width: 0;
`;

const TileLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font: 600 10.5px/1 'Figtree', sans-serif;
  color: ${inkLight};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const TileText = styled.div<{ valueColor?: string }>`
  font: 600 14px/1.25 'Figtree', sans-serif;
  color: ${(props) => props.valueColor ?? inkDarkest};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TileValue = styled.div<{ valueColor?: string }>`
  font: 600 22px/1.1 'Figtree', sans-serif;
  color: ${(props) => props.valueColor ?? inkDarkest};
  font-variant-numeric: tabular-nums;
`;

const TileSub = styled.div`
  font: 400 11px/1 'Figtree', sans-serif;
  color: ${inkLighter};
`;
